import { useState } from 'react';

// Keyed by whatever code the data carries. The CSV uses ISO country codes while
// email regions use Amazon's own (UK, not GB), so both spellings are listed —
// GB, IE and BE were missing entirely and rendered as a globe.
const REGION_FLAGS = {
  DE: '🇩🇪', FR: '🇫🇷', IT: '🇮🇹', NL: '🇳🇱', PT: '🇵🇹', BE: '🇧🇪', IE: '🇮🇪',
  UK: '🇬🇧', GB: '🇬🇧', ES: '🇪🇸', SE: '🇸🇪', PL: '🇵🇱', AT: '🇦🇹', CH: '🇨🇭',
  DK: '🇩🇰', NO: '🇳🇴', FI: '🇫🇮', CZ: '🇨🇿', RO: '🇷🇴', HU: '🇭🇺', GR: '🇬🇷',
  US: '🇺🇸', CA: '🇨🇦', MX: '🇲🇽', BR: '🇧🇷', CO: '🇨🇴', AR: '🇦🇷', CL: '🇨🇱',
  AU: '🇦🇺', JP: '🇯🇵', SG: '🇸🇬', AE: '🇦🇪', IN: '🇮🇳', TR: '🇹🇷', ZA: '🇿🇦',
  PH: '🇵🇭', ID: '🇮🇩', KE: '🇰🇪', EE: '🇪🇪', NZ: '🇳🇿',
};

// Dimensions whose keys are country codes, so they get a flag. Origin country
// (from the CSV) and marketplace (from the sending Amazon site) are different
// facts and are shown as separate dimensions.
const COUNTRY_DIMS = new Set(['region', 'marketplace']);

// Header, rows and drill-down rows must stay in step — one source per grid.
// The first column is the row's NAME, and it must never be squeezed away. It was
// written as a bare 1fr, which yields to the fixed columns beside it: at a
// ~1180px viewport three cards fit across, the fixed columns (368px plus 60px of
// gaps) exceeded the card, and the name column resolved to a negative width —
// so every domain and country label rendered blank while the numbers stayed.
// A minmax floor makes the row overflow instead of silently losing its label.
const gridCols    = m => m ? 'minmax(64px,1fr) 46px 46px 42px 46px' : 'minmax(100px,1fr) 68px 72px 58px 104px 66px';
const subGridCols = m => m ? 'minmax(64px,1fr) 46px 46px 42px'      : 'minmax(100px,1fr) 68px 72px 58px 92px';

// Anything under an hour used to floor to "1h", which hid the most striking
// thing in the data: Polish accounts have a median survival of 2.9 minutes and
// German ones 42.8 minutes, and both displayed as "1h".
function fmtSurvival(days) {
  if (days === null || days === undefined) return '—';
  const mins = days * 1440;
  if (mins < 1)  return `${Math.round(days * 86400)}s`;
  if (mins < 90) return `${mins < 10 ? mins.toFixed(1) : Math.round(mins)}m`;
  if (days < 1)  return `${(days * 24).toFixed(1)}h`;
  return `${days.toFixed(1)}d`;
}

function fmtAge(iso) {
  const h = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} minutes ago`;
  if (h < 48) return `${Math.round(h)} hours ago`;
  return `${Math.round(h / 24)} days ago`;
}

function fmtRate(rate) {
  if (rate === null || rate === undefined) return '—';
  return `${(rate * 100).toFixed(rate >= 0.1 ? 0 : 1)}%`;
}

// Red at high ban rates, amber mid, green low — instant read of severity.
// null rate (bans but no created accounts in the CSV) reads as an anomaly → red.
function rateColor(rate) {
  if (rate === null || rate === undefined || rate >= 0.5) return '#f87171';
  if (rate >= 0.15) return '#fbbf24';
  return '#34d399';
}

function KpiCard({ label, value, sub, accent, isMobile }) {
  return (
    <div style={{
      background: 'rgba(0,0,0,0.25)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 12,
      padding: isMobile ? '12px 14px' : '16px 18px',
      flex: 1,
      minWidth: isMobile ? 130 : 160,
    }}>
      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 700, color: accent || '#f1f5f9', marginTop: 4, letterSpacing: '-0.5px' }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function GroupRow({ group, dimId, maxBanned, isMobile, trackedDomains }) {
  const [expanded, setExpanded] = useState(false);
  // Only meaningful for the domain dimension — a region spans many domains
  const untracked = dimId === 'domain' && trackedDomains?.length > 0 && !trackedDomains.includes(group.key);
  const flag = COUNTRY_DIMS.has(dimId) ? (REGION_FLAGS[group.key] || '🌐') : null;
  const color = rateColor(group.banRate);
  const isUnknown = group.key === 'unknown';
  const canExpand = (group.breakdown || []).some(b => b.groups.length > 0);

  return (
    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <div
        onClick={() => canExpand && setExpanded(e => !e)}
        style={{
          display: 'grid',
          gridTemplateColumns: gridCols(isMobile),
          gap: isMobile ? 8 : 12,
          alignItems: 'center',
          padding: isMobile ? '9px 10px' : '10px 14px',
          opacity: isUnknown ? 0.55 : 1,
          cursor: canExpand ? 'pointer' : 'default',
          background: expanded ? 'rgba(255,255,255,0.03)' : 'transparent',
          transition: 'background 0.12s',
        }}
      >
        {/* Name */}
        <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {canExpand && (
            <span style={{
              display: 'inline-block', marginRight: 7, fontSize: 9, color: '#64748b',
              transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s',
            }}>▶</span>
          )}
          {flag && <span style={{ marginRight: 6 }}>{flag}</span>}
          {group.key}
          {/* Advertises that this row opens onto something, and how much. A
              domain with subdomains otherwise looks identical to one without. */}
          {group.subdomainCount > 0 && (
            <span
              title={`${group.subdomainCount} subdomain${group.subdomainCount === 1 ? '' : 's'} — click to see creations and suspensions for each`}
              style={{
                marginLeft: 7, fontSize: 10, fontWeight: 600,
                background: 'rgba(148,163,184,0.15)', color: '#94a3b8',
                borderRadius: 20, padding: '1px 7px', whiteSpace: 'nowrap',
              }}
            >
              {group.subdomainCount} sub{group.subdomainCount === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {/* Accounts created in the window. A domain outside trackedDomains never
            gets a first-seen date recorded, so its 0 means "not measured" — say
            so rather than let it read as no signups. */}
        <div style={{ fontSize: 12, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                      color: untracked ? '#475569' : group.created ? '#38bdf8' : '#475569',
                      fontWeight: group.created ? 600 : 400 }}
             title={untracked
               ? `${group.key} is not in trackedDomains, so no creation dates are recorded for it — this is "not measured", not zero`
               : `${group.created} account${group.created === 1 ? '' : 's'} first seen in this window`}>
          {untracked ? <span style={{ opacity: 0.65 }}>n/a</span> : group.created.toLocaleString()}
        </div>

        {/* Bans / total */}
        <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          <span style={{ color: '#f87171', fontWeight: 600 }}>{group.banned}</span>
          <span style={{ color: '#475569' }}>/{group.total}</span>
        </div>

        {/* Ban rate number */}
        <div style={{ fontSize: 12, fontWeight: 700, color, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {fmtRate(group.banRate)}
        </div>

        {/* Ban rate bar (desktop only) */}
        {!isMobile && (
          <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.min(100, (group.banRate ?? 1) * 100)}%`,
              height: '100%', background: color, borderRadius: 3,
              transition: 'width 0.4s ease-out',
            }} />
          </div>
        )}

        {/* Median survival */}
        <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
             title={group.survivalSamples ? `median of ${group.survivalSamples} measurable bans` : 'no survival data'}>
          {fmtSurvival(group.medianSurvivalDays)}
        </div>
      </div>

      {/* Drill-down: this group broken down by each other dimension */}
      {expanded && (group.breakdown || []).map(sub => (
        sub.groups.length > 0 && (
          <div key={sub.id} style={{
            padding: isMobile ? '4px 10px 10px 24px' : '4px 14px 12px 34px',
            background: 'rgba(0,0,0,0.2)',
            animation: 'fadeIn 0.15s ease-out',
          }}>
            <div style={{ fontSize: 10, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '6px 0 4px' }}>
              By {sub.label}
              {sub.id === 'subdomain' && (
                <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: '#334155' }}>
                  {' '}· includes the bare domain
                </span>
              )}
            </div>

            {/* The drill-down numbers were unlabelled, so a reader had to match
                them against the parent table by position to know which column
                was creations and which was bans. */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: subGridCols(isMobile),
              gap: isMobile ? 8 : 12,
              fontSize: 9, color: '#334155', fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.5px',
              padding: '0 0 3px',
            }}>
              <span>{sub.label}</span>
              <span style={{ textAlign: 'right' }}>Created</span>
              <span style={{ textAlign: 'right' }}>Bans</span>
              <span style={{ textAlign: 'right' }}>Rate</span>
              {!isMobile && <span />}
            </div>
            {sub.groups.map(sg => {
              const sgFlag  = COUNTRY_DIMS.has(sub.id) ? (REGION_FLAGS[sg.key] || '🌐') : null;
              const sgColor = rateColor(sg.banRate);
              return (
                <div key={sg.key} style={{
                  display: 'grid',
                  gridTemplateColumns: subGridCols(isMobile),
                  gap: isMobile ? 8 : 12,
                  alignItems: 'center',
                  padding: '5px 0',
                  opacity: sg.key === 'unknown' ? 0.55 : 1,
                }}>
                  <div style={{ fontSize: 12, color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {sgFlag && <span style={{ marginRight: 6 }}>{sgFlag}</span>}
                    {sg.key}
                  </div>
                  <div style={{ fontSize: 11, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                color: sg.created ? '#38bdf8' : '#475569', fontWeight: sg.created ? 600 : 400 }}>
                    {sg.created.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    <span style={{ color: '#f87171', fontWeight: 600 }}>{sg.banned}</span>
                    <span style={{ color: '#475569' }}>/{sg.total}</span>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: sgColor, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtRate(sg.banRate)}
                  </div>
                  {!isMobile && (
                    <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min(100, (sg.banRate ?? 1) * 100)}%`,
                        height: '100%', background: sgColor, borderRadius: 2,
                      }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ))}
    </div>
  );
}

function DimensionCard({ dimension, isMobile, trackedDomains }) {
  const groups = dimension.groups || [];
  const maxBanned = Math.max(1, ...groups.map(g => g.banned));

  return (
    <div style={{
      background: 'rgba(0,0,0,0.25)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 12,
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: isMobile ? '11px 12px' : '13px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        background: 'rgba(255,255,255,0.02)',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>By {dimension.label}</span>
        <span style={{ fontSize: 11, color: '#475569' }}>worst first · click a row to drill down</span>
      </div>

      {/* Column headers */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: gridCols(isMobile),
        gap: isMobile ? 8 : 12,
        padding: isMobile ? '7px 10px' : '7px 14px',
        fontSize: 10, color: '#475569', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.5px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
      }}>
        <span>{dimension.label}</span>
        <span style={{ textAlign: 'right', color: '#38bdf8' }} title="Accounts first seen inside the selected window">
          {isMobile ? 'New' : 'Created'}
        </span>
        <span style={{ textAlign: 'right' }}>Bans</span>
        <span style={{ textAlign: 'right' }}>Rate</span>
        {!isMobile && <span />}
        <span style={{ textAlign: 'right' }}>Survival</span>
      </div>

      {groups.map(g => (
        <GroupRow key={g.key} group={g} dimId={dimension.id} maxBanned={maxBanned} isMobile={isMobile} trackedDomains={trackedDomains} />
      ))}
      {!groups.length && (
        <div style={{ padding: 20, fontSize: 12, color: '#475569', textAlign: 'center' }}>No data yet</div>
      )}
    </div>
  );
}

// Mirrors the backend's WINDOWS list; the server's own list is preferred when
// the response carries one, so adding a window there needs no change here.
const FALLBACK_WINDOWS = [
  { key: 'all', label: 'All time'      },
  { key: '1h',  label: 'Last hour'     },
  { key: '6h',  label: 'Last 6 hours'  },
  { key: '24h', label: 'Last 24 hours' },
  { key: '72h', label: 'Last 72 hours' },
  { key: '7d',  label: 'Last 7 days'   },
];

function WindowPicker({ windows, value, onChange, isMobile }) {
  return (
    <div style={{
      display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center',
      background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 10, padding: 4, marginBottom: isMobile ? 12 : 16,
    }}>
      <span style={{ fontSize: 11, color: '#475569', padding: '0 8px', fontWeight: 500 }}>Bans in</span>
      {windows.map(w => {
        const active = value === w.key;
        return (
          <button
            key={w.key}
            onClick={() => onChange(w.key)}
            style={{
              fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 7,
              border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
              background: active ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'transparent',
              color: active ? '#fff' : '#64748b',
              boxShadow: active ? '0 2px 8px rgba(99,102,241,0.35)' : 'none',
              transition: 'all 0.12s',
            }}
          >
            {w.label}
          </button>
        );
      })}
    </div>
  );
}

export default function HealthHub({ analysis, window: windowKey = 'all', onWindow, isMobile }) {
  if (!analysis || !analysis.overall) {
    return (
      <div style={{ padding: 30, textAlign: 'center', color: '#475569', fontSize: 13 }}>
        Loading analysis…
      </div>
    );
  }

  const { overall, dimensions } = analysis;
  const windows   = analysis.windows?.length ? analysis.windows : FALLBACK_WINDOWS;
  const windowed  = windowKey !== 'all';
  const winLabel  = (analysis.window?.label || windows.find(w => w.key === windowKey)?.label || 'All time').toLowerCase();

  return (
    <div>
      {onWindow && (
        <WindowPicker windows={windows} value={windowKey} onChange={onWindow} isMobile={isMobile} />
      )}

      {/* The CSV is the record of creation, so a window can legitimately show no
          new accounts while the inbox is already seeing them. Say so, rather
          than leaving a bare zero that reads as a fault. */}
      {windowed && overall.pendingConfirmation > 0 && (
        <div style={{
          background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.22)',
          borderRadius: 10, padding: '10px 14px', fontSize: 12, marginBottom: 14,
          color: '#7dd3fc', lineHeight: 1.6,
        }}>
          <strong>{overall.pendingConfirmation.toLocaleString()} address{overall.pendingConfirmation === 1 ? '' : 'es'}</strong>
          {' '}have appeared in the inbox since the CSV was exported
          {overall.csvExportedAt && <> {fmtAge(overall.csvExportedAt)}</>}.
          {overall.createdInWindow === 0
            ? ' They are why this window shows no creations: an account counts as created once it is in the CSV, and these are not in it yet.'
            : ' They are not counted as created yet — an account counts once it is in the CSV.'}
          {' '}Re-import to confirm them.
        </div>
      )}

      {/* ── KPI row ─────────────────────────────────── */}
      <div style={{ display: 'flex', gap: isMobile ? 8 : 12, flexWrap: 'wrap', marginBottom: isMobile ? 14 : 20 }}>
        <KpiCard
          label={windowed ? `Created · ${winLabel}` : 'Accounts created'}
          value={(windowed ? overall.createdInWindow ?? 0 : overall.totalAccounts).toLocaleString()}
          sub={windowed
            ? `of ${overall.totalAccounts.toLocaleString()} in CSV`
              + (overall.pendingConfirmation ? ` · ${overall.pendingConfirmation.toLocaleString()} awaiting export` : '')
              + (overall.createdEstimatedInWindow ? ` · ${overall.createdEstimatedInWindow.toLocaleString()} estimated` : '')
              + (overall.createdUndated ? ` · ${overall.createdUndated.toLocaleString()} undatable` : '')
            : 'from CSV import'}
          accent={windowed ? '#38bdf8' : undefined}
          isMobile={isMobile}
        />
        {/* The headline is the like-for-like figure: CSV accounts that were
            banned, over CSV accounts. The all-in count is shown beneath it,
            because its numerator includes bans on addresses the denominator
            never contained, which is what let the rate read high. */}
        <KpiCard
          label={windowed ? `Banned · ${winLabel}` : 'Banned'}
          value={(overall.bannedInCsv ?? overall.banned).toLocaleString()}
          sub={`${fmtRate(overall.banRateInCsv ?? overall.banRate)} of the ${overall.totalAccounts.toLocaleString()} accounts in the CSV`
            + (overall.bansOutsideCsv ? ` · plus ${overall.bansOutsideCsv.toLocaleString()} banned that are not in it` : '')}
          accent={overall.banned > 0 ? '#f87171' : '#34d399'}
          isMobile={isMobile}
        />
        <KpiCard
          label="Median survival"
          value={fmtSurvival(overall.medianSurvivalDays)}
          sub={`from ${overall.survivalSamples} measurable ban${overall.survivalSamples === 1 ? '' : 's'}`}
          accent="#fbbf24"
          isMobile={isMobile}
        />
        {/* Redundant once a window is active — the Banned card already is that count */}
        {!windowed && (
          <KpiCard
            label="Bans · last 7 days"
            value={overall.bansLast7Days.toLocaleString()}
            accent={overall.bansLast7Days > 0 ? '#f87171' : '#34d399'}
            isMobile={isMobile}
          />
        )}
      </div>

      {/* ── Dimension breakdowns — rendered generically so new backend
             dimensions appear here automatically ────── */}
      <div style={{
        display: 'grid',
        // 560px, not 340px: a card narrower than its own fixed columns cannot
        // show a row label. Three dimensions now exist, and at 340px three of
        // them crammed onto a 1180px screen and blanked every name.
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(560px, 1fr))',
        gap: isMobile ? 12 : 16,
      }}>
        {dimensions.map(d => (
          <DimensionCard key={d.id} dimension={d} isMobile={isMobile} trackedDomains={analysis.trackedDomains} />
        ))}
      </div>

      <p style={{ fontSize: 11, color: '#475569', marginTop: 14, lineHeight: 1.5 }}>
        Created accounts are derived from the CSV import only — inbox activity (e.g. OTP) never adds accounts.
        Bans count every suspension email scraped, including accounts no longer in the current CSV, so a rate can exceed 100%.
        Survival = time from the account's first observed Amazon email to its suspension email.
        {windowed && (
          <> The window filters bans and creations; the ban <em>rate</em> still divides by the whole
          created-account base, so it reads as share of the fleet lost in the <strong style={{ color: '#94a3b8' }}>{winLabel}</strong>.
          Created counts CSV accounts whose first Amazon email falls in the window. The CSV decides whether an
          account exists; the email supplies the date, since the CSV's own import timestamp is identical for
          every row and cannot date anything.
          {overall.pendingConfirmation > 0 && (
            <> An address seen in the inbox but absent from the CSV is
            <strong style={{ color: '#94a3b8' }}> not counted as created</strong> — the CSV is the record of what
            was made, the email only matches a row and dates it. {overall.pendingConfirmation.toLocaleString()} such
            address{overall.pendingConfirmation === 1 ? ' is' : 'es are'} waiting on the next export, so recent
            windows lag the inbox by however old that export is.</>
          )}
          {overall.createdEstimatedInWindow > 0 && (
            <> {overall.createdEstimatedInWindow.toLocaleString()} of them are
            <strong style={{ color: '#94a3b8' }}> estimated</strong>: no email ever arrived for the address, so the
            date comes from its position in the CSV, which is ordered oldest first. Those are placed between
            their nearest dated neighbours rather than observed.</>
          )}
          {overall.createdUndated > 0 && (
            <> {overall.createdUndated.toLocaleString()} CSV accounts carry no such date and can never appear
            in a window: a first-seen date is only recorded for recipients on a <strong style={{ color: '#94a3b8' }}>tracked
            domain</strong>, and tracking starts from the moment a domain is known — it cannot backfill.
            The tracked set is now derived from the account CSV itself
            {analysis.trackedDomains?.length ? ` (${analysis.trackedDomains.length} domains)` : ''}, so a new
            domain counts as soon as it is imported rather than waiting to be added to a list by hand.</>
          )}</>
        )}
        {' '}Domains are grouped by the name that was registered, so
        <strong style={{ color: '#94a3b8' }}> news.hislimpole.org</strong> sits inside
        <strong style={{ color: '#94a3b8' }}> hislimpole.org</strong> — click a domain with a
        <strong style={{ color: '#94a3b8' }}> subs</strong> badge to see creations and suspensions per subdomain.
        Public suffixes are respected, so elkkicks.co.uk and hanclemin.it.com stay whole rather than being read
        as subdomains of co.uk and it.com.
      </p>
    </div>
  );
}
